import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import * as auth from "./auth";

// Thin React wrapper over lib/auth.js — holds UI state (user) and runs the mount-time
// session restore. All token/refresh mechanics live in auth.js so non-hook callers can
// share them. Ported from pollis-campaign-os, with campaign selection removed.
const AuthContext = createContext(null);

// Read the handoff token off the URL once, so a magic link from another Pollis surface
// signs the user straight in. Origin-independent, unlike localStorage.
const readHandoff = () => {
  const params = new URLSearchParams(window.location.search);
  const token = params.get("token");
  const uid = params.get("uid");
  if (token && uid) return { token, uid };
  const legacy = params.get("magic_token");
  if (legacy) return { token: legacy, uid: null };
  return null;
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const handoff = useRef(readHandoff());

  const applyAuth = useCallback((data) => {
    setUser({ ...data.user, superuser: !!data.user?.superuser });
  }, []);

  const applyMe = useCallback((data) => {
    // /auth/me returns campaign data for Pollis surfaces; LandPilot ignores it.
    const { campaigns, ...userFields } = data;
    void campaigns;
    setUser({ ...userFields, superuser: !!userFields.superuser });
  }, []);

  const login = useCallback(
    async (payload) => {
      const data = await auth.login(payload);
      applyAuth(data);
      return data;
    },
    [applyAuth],
  );

  const verifyMagicToken = useCallback(
    async (token, uid) => {
      const data = await auth.verifyMagicToken(token, uid);
      applyAuth(data);
      return data;
    },
    [applyAuth],
  );

  const logout = useCallback(() => {
    auth.logout();
    setUser(null);
  }, []);

  // Any 401 that survives a refresh drops the app to sign-in. Without this the tokens are
  // cleared but `user` stays set, so the shell keeps rendering over a dead session.
  useEffect(
    () =>
      auth.onSessionExpired(() => {
        setUser(null);
      }),
    [],
  );

  // Mount: handoff token → stored access token → refresh.
  useEffect(() => {
    (async () => {
      if (handoff.current) {
        try {
          await verifyMagicToken(handoff.current.token, handoff.current.uid);
          window.history.replaceState({}, "", window.location.pathname);
          setLoading(false);
          return;
        } catch {
          /* fall through to a normal restore */
        }
      }
      const me = await auth.fetchMe();
      if (me) applyMe(me);
      setLoading(false);
    })();
  }, [verifyMagicToken, applyMe]);

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        logout,
        verifyMagicToken,
        isLoggedIn: !!user,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
};
